import AdminLayout from "@/layouts/AdminLayout";
import { useNotifications } from "@/context/NotificationContext";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import TeamMemberModal from "./components/TeamMemberModal";
import { useState, useEffect } from "react";
import api from "@/services/api";

interface TeamMember {

  id: string;
  full_name: string;
  email: string;
  role: string;
  job_title: string | null;
  avatar_url: string | null;
  created_at: string;
  projects: number;
}

export default function AdminTeam() {
  const { showToast } = useNotifications();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);

  useEffect(() => {
    setLoading(true);
    api.get("/users/team")
      .then((res) => setMembers(res.data.data || []))
      .catch(() => showToast("error", "Failed to load team members"))
      .finally(() => setLoading(false));
  }, [showToast]);

  const roleColors: Record<string, string> = {
    admin: "text-cyber-red border-cyber-red/30 bg-cyber-red/10",
    staff: "text-electric-blue border-electric-blue/30 bg-electric-blue/10",
    client: "text-silver border-dark-border bg-dark-border/30",
  };

  const handleInvite = async (payload: any) => {
    try {
      // payload includes password from the modal
      await api.post("/users", payload);

      showToast("success", "Team member invited.");
      // Refresh list
      api.get("/users/team").then((res) => setMembers(res.data.data || []));
      setInviteOpen(false);
    } catch (err: any) {
      throw new Error(err?.response?.data?.message || "Failed to invite team member.");
    }
  };

  const handleEdit = async (payload: any) => {
    if (!editingMember) return;
    try {
      await api.patch(`/users/${editingMember.id}`, payload);
      showToast("success", "Team member updated.");
      // Refresh list
      api.get("/users/team").then((res) => setMembers(res.data.data || []));
      setEditOpen(false);
      setEditingMember(null);
    } catch (err: any) {
      throw new Error(err?.response?.data?.message || "Failed to update team member.");
    }
  };

  const openEditModal = (member: TeamMember) => {
    setEditingMember(member);
    setEditOpen(true);
  };

  const handleRemove = async () => {
    if (!removeId) return;
    try {
      await api.delete(`/users/${removeId}`);
      setMembers((prev) => prev.filter((m) => m.id !== removeId));
      showToast("success", "Team member removed.");
    } catch {
      showToast("error", "Failed to remove team member.");
    } finally {
      setRemoveId(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Team Management
            </h1>
            <p className="text-silver text-sm mt-1">{members.length} team members</p>
          </div>
          <button
            onClick={() => setInviteOpen(true)}
            className="flex items-center gap-2 bg-electric-blue text-deep-black font-bold px-5 py-2.5 rounded-xl text-sm hover:shadow-glow-blue transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" /> Invite Member
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {members.map((member) => (
            <div
              key={member.id}
              className="bg-dark-card border border-dark-border rounded-2xl p-5 hover:border-electric-blue/20 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-electric-blue/10 border border-electric-blue/30 text-electric-blue text-sm font-bold flex-shrink-0">
                    {member.full_name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">{member.full_name}</p>
                    <p className="text-silver-dim text-xs">{member.email}</p>
                  </div>
                </div>
                <span
                  className={`text-xs px-2.5 py-1 rounded-full border capitalize whitespace-nowrap ${roleColors[member.role] || roleColors.client}`}
                >
                  {member.role}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                {member.job_title && (
                  <div className="flex items-center gap-2 text-xs text-silver-dim">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-briefcase-line text-electric-blue text-xs" />
                    </div>
                    {member.job_title}
                  </div>
                )}
                <div className="flex items-center gap-2 text-xs text-silver-dim">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-folder-line text-electric-blue text-xs" />
                  </div>
                  {member.projects} active projects
                </div>
                <div className="flex items-center gap-2 text-xs text-silver-dim">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line text-electric-blue text-xs" />
                  </div>
                  Joined{" "}
                  {new Date(member.created_at).toLocaleDateString("en-US", {
                    month: "short",
                    year: "numeric",
                  })}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => openEditModal(member)}
                  className="flex-1 py-2 rounded-lg border border-dark-border text-silver hover:text-white hover:border-electric-blue/40 text-xs transition-all cursor-pointer whitespace-nowrap"
                >
                  Edit
                </button>
                <button
                  onClick={() => setRemoveId(member.id)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-dark-border text-silver-dim hover:text-cyber-red hover:border-cyber-red/30 transition-all cursor-pointer flex-shrink-0"
                >
                  <i className="ri-delete-bin-line text-xs" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <TeamMemberModal
        open={inviteOpen}
        mode="create"
        onClose={() => setInviteOpen(false)}
        onSubmit={handleInvite}
      />
      <TeamMemberModal


        open={editOpen}
        mode="edit"
        initial={editingMember ? {
          full_name: editingMember.full_name,
          email: editingMember.email,
          role: editingMember.role as "admin" | "staff",
          job_title: editingMember.job_title || "",
        } : null}
        onClose={() => {
          setEditOpen(false);
          setEditingMember(null);
        }}
        onSubmit={handleEdit}
      />
      <ConfirmDialog
        open={!!removeId}
        title="Remove Team Member"
        message="Are you sure you want to remove this team member? They will lose access to the platform."
        confirmLabel="Remove"
        danger
        onConfirm={handleRemove}
        onCancel={() => setRemoveId(null)}
      />
    </AdminLayout>
  );
}

